import type { TFunction } from 'i18next';

import {
  CreateUpdateJobConfigDto,
  CreateUpdateJobConfigDtoFromJSON,
  DefaultApi,
  GetJobConfigDto,
  GetLastEditorDto,
} from '../autogenerated/pokApiClient';
import { validateAgainst } from '../validation/validateAgainst';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { JobConfigSchema } from '../validation/schemas';
import { jobConfigForGrid } from '../../../pok/components/JobConfig/JobConfigSearch';
import { FilterYearReportData } from '../../../pok/components/Reports/types/FilterYearTypes';

export interface JobConfigContextInterface {
  getById: (id: string) => Promise<GetJobConfigDto>;
  getByJobName: (
    jobName: string,
    language: 'pl' | 'en',
  ) => Promise<GetJobConfigDto>;
  getAllByPortion: (
    t: TFunction<'administrationLng', 'administrationLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    language?: 'pl' | 'en',
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  update: (id: string, dto: CreateUpdateJobConfigDto) => Promise<void>;
  create: (dto: CreateUpdateJobConfigDto) => Promise<GetJobConfigDto>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  synchronizeAllJobs: () => Promise<void>;

  bulkDownloadsDocuments: (filter: FilterYearReportData) => Promise<void>;
}

export const JobConfigContext = (api: DefaultApi) => {
  return {
    getById: (id: string) => api.jobConfigControllerGet(id),
    getByJobName: (jobName: string, language: 'pl' | 'en') =>
      api.jobConfigControllerGetByJobName(jobName, language),
    getAllByPortion: async (
      t: TFunction<'administrationLng', 'administrationLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      language: 'pl' | 'en' = 'pl',
    ) => {
      const data = await api.jobConfigControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        (orderDirection as 'ASC' | 'DESC') || 'ASC',
        filterText || '',
        language,
        filterColumns || '',
      );

      return data.map(item => jobConfigForGrid(t, item));
    },
    getAllCount: async (filterText?: string, filterColumns?: string) =>
      api.jobConfigControllerGetAllCount(filterText || '', filterColumns),
    update: (id: string, dto: CreateUpdateJobConfigDto) =>
      api.jobConfigControllerUpdate(id, dto),
    create: (dto: CreateUpdateJobConfigDto) =>
      api.jobConfigControllerCreate(dto),
    getLastEditor: (id: string) => api.jobConfigControllerGetLastEditor(id),
    synchronizeAllJobs: () => api.jobConfigControllerSynchronizeAllJobs(),
    bulkDownloadsDocuments: async (filter: FilterYearReportData) => {
      return await api.jobConfigControllerBulkDownloadsDocuments({
        ...filter,
      });
    },
  };
};

export const newJobConfig = () => {
  return CreateUpdateJobConfigDtoFromJSON({
    teamIds: [],
    emails: [],
  });
};

export const validate = (jobConfig: CreateUpdateJobConfigDto) => {
  return validateAgainst(JobConfigSchema, jobConfig);
};

export const convert = (jobConfig?: GetJobConfigDto) => {
  if (!jobConfig) {
    return newJobConfig();
  }
  const converted = CreateUpdateJobConfigDtoFromJSON(jobConfig);

  converted.companyId = jobConfig.company?.id;
  converted.teamIds =
    jobConfig.jobConfigTeams?.map(({ team }) => team.id) || [];

  return converted;
};
